import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { Wrench, MagnifyingGlass, X, CaretDown, CaretRight, CheckCircle, Plus, Pencil, Trash, Clock } from "@phosphor-icons/react";
import { regSort } from "@/lib/csv";
import { formatDate } from "@/lib/format";
import ReportToolbar, { ZoomScope, ZOOM_DEFAULT } from "@/components/ReportToolbar";
import WoExportMenu from "@/components/WoExportMenu";
import WorkDescriptionsEditor from "@/components/WorkDescriptionsEditor";
import { exportWosCsv, exportWosPdf } from "@/lib/woExport";

const TYPES = ["preventive", "breakdown", "inspection"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyParent = { vehicle_id: "", creation_date: todayISO(), notes: "", descriptions: [] };
const emptySub = { title: "", type: "preventive", priority: "medium", start_date: todayISO(), descriptions: [] };

export default function WorkOrders() {
    const [parents, setParents] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [expanded, setExpanded] = useState({}); // parentId → bool

    // Create-parent + edit-parent
    const [parentOpen, setParentOpen] = useState(false);
    const [parentForm, setParentForm] = useState(emptyParent);
    const [editingParent, setEditingParent] = useState(null);

    // Create-sub + edit-sub
    const [subOpen, setSubOpen] = useState(false);
    const [subForm, setSubForm] = useState({ ...emptySub, parent_id: "", vehicle_id: "" });
    const [editingSub, setEditingSub] = useState(null);

    // Finish sub
    const [finishFor, setFinishFor] = useState(null);
    const [finishDate, setFinishDate] = useState(todayISO());

    // Issue parts modal
    const [issueFor, setIssueFor] = useState(null);
    const [issuePart, setIssuePart] = useState({ inventory_id: "", quantity: 1 });

    // Edit part line
    const [editingPart, setEditingPart] = useState(null); // { subId, idx, line }
    const [editPartForm, setEditPartForm] = useState({ quantity: 1, unit_cost: 0 });

    // Filters
    const [search, setSearch] = useState("");
    const [filterVehicle, setFilterVehicle] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");

    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(ZOOM_DEFAULT);

    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        if (filterVehicle) params.set("vehicle_id", filterVehicle);
        if (filterStatus) params.set("status", filterStatus);
        if (filterFrom) params.set("date_from", filterFrom);
        if (filterTo) params.set("date_to", filterTo);
        return params;
    }, [search, filterVehicle, filterStatus, filterFrom, filterTo]);

    const load = useCallback(async (targetPage) => {
        const tp = targetPage ?? 1;
        setBusy(true);
        try {
            const params = buildFilterParams();
            params.set("page", String(tp));
            params.set("page_size", String(PAGE_SIZE));
            const r = await api.get(`/work-orders/tree?${params.toString()}`);
            const payload = r.data || {};
            setParents(payload.items || []);
            setTotal(payload.total ?? 0);
            setPage(payload.page ?? tp);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load work orders");
        } finally {
            setBusy(false);
        }
    }, [buildFilterParams]);

    useEffect(() => {
        load(1);
        api.get("/vehicles").then((r) => setVehicles(r.data));
        api.get("/inventory").then((r) => setInventory(Array.isArray(r.data) ? r.data : (r.data?.items || [])));
    }, [load]);

    // Refetch when filters change → reset to page 1
    useEffect(() => {
        setPage(1);
        load(1);
    }, [search, filterVehicle, filterStatus, filterFrom, filterTo, load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const vehMap = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);

    // ----- Parent CRUD -----
    const createParent = async () => {
        if (!parentForm.vehicle_id) { toast.error("Pick a vehicle"); return; }
        try {
            await api.post("/work-orders/parents", parentForm);
            toast.success("Main work order created");
            setParentOpen(false); setParentForm(emptyParent); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const startEditParent = (p) => {
        setEditingParent(p.id);
        setParentForm({
            vehicle_id: p.vehicle_id,
            creation_date: p.creation_date || todayISO(),
            notes: p.notes || "",
            descriptions: Array.isArray(p.descriptions) ? p.descriptions : [],
        });
        setParentOpen(true);
    };
    const saveParent = async () => {
        if (!editingParent) return createParent();
        try {
            await api.put(`/work-orders/parents/${editingParent}`, parentForm);
            toast.success("Main work order updated");
            setParentOpen(false); setParentForm(emptyParent); setEditingParent(null); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const deleteParent = async (p) => {
        if (!window.confirm(`Delete main WO ${p.wo_number}?\nThis also removes all ${p.sub_task_count} sub-task(s).`)) return;
        try {
            await api.delete(`/work-orders/parents/${p.id}`);
            toast.success("Main work order deleted"); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    // ----- Sub-task CRUD -----
    const openAddSub = (p) => {
        setSubForm({ ...emptySub, parent_id: p.id, vehicle_id: p.vehicle_id });
        setEditingSub(null); setSubOpen(true);
    };
    const openEditSub = (p, s) => {
        setSubForm({
            parent_id: p.id, vehicle_id: p.vehicle_id,
            title: s.title || "",
            type: s.type || "preventive", priority: s.priority || "medium",
            start_date: s.start_date || todayISO(),
            descriptions: Array.isArray(s.descriptions) ? s.descriptions : [],
        });
        setEditingSub(s.id); setSubOpen(true);
    };
    const saveSub = async () => {
        if (!subForm.title?.trim()) { toast.error("Title required"); return; }
        try {
            if (editingSub) {
                await api.patch(`/work-orders/${editingSub}`, {
                    title: subForm.title,
                    descriptions: subForm.descriptions || [],
                    type: subForm.type, priority: subForm.priority,
                    start_date: subForm.start_date,
                });
                toast.success("Sub-task updated");
            } else {
                await api.post("/work-orders", subForm);
                toast.success("Sub-task added");
            }
            setSubOpen(false); setSubForm({ ...emptySub, parent_id: "", vehicle_id: "" }); setEditingSub(null); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const deleteSub = async (s) => {
        if (!window.confirm(`Delete sub-task ${s.wo_number}?`)) return;
        try { await api.delete(`/work-orders/${s.id}`); toast.success("Sub-task deleted"); load(page); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const finishSub = async () => {
        try {
            await api.patch(`/work-orders/${finishFor.id}`, { status: "done", end_date: finishDate });
            toast.success(`Sub-task ${finishFor.wo_number} finished`);
            setFinishFor(null); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const reopenSub = async (s) => {
        try { await api.patch(`/work-orders/${s.id}`, { status: "in_progress" }); load(page); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    // ----- Parts -----
    const issueParts = async () => {
        if (!issueFor) return;
        if (!issuePart.inventory_id || !Number(issuePart.quantity)) { toast.error("Pick a part and quantity"); return; }
        try {
            await api.post("/inventory/issue", {
                work_order_id: issueFor.id,
                vehicle_id: issueFor.vehicle_id,
                items: [{ inventory_id: issuePart.inventory_id, quantity: Number(issuePart.quantity) }],
            });
            toast.success("Part issued");
            setIssuePart({ inventory_id: "", quantity: 1 });
            // Refresh both inventory and tree (keep current page)
            api.get("/inventory").then((r) => setInventory(Array.isArray(r.data) ? r.data : (r.data?.items || [])));
            await load(page);
            // Re-fetch the modal's parent to surface the new part line
            try {
                const t = await api.get(`/work-orders/tree?page=${page}&page_size=${PAGE_SIZE}`);
                const updatedSub = (t.data?.items || []).flatMap((p) => p.sub_tasks).find((s) => s.id === issueFor.id);
                if (updatedSub) setIssueFor(updatedSub);
            } catch (err) { /* non-fatal */ }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const openEditPart = (subId, idx, line) => {
        setEditingPart({ subId, idx, line });
        setEditPartForm({ quantity: line.quantity || 1, unit_cost: line.unit_cost || 0 });
    };
    const saveEditPart = async () => {
        const { subId, idx } = editingPart;
        try {
            await api.put(`/work-orders/${subId}/parts/${idx}`, {
                quantity: Number(editPartForm.quantity),
                unit_cost: Number(editPartForm.unit_cost),
            });
            toast.success("Part line updated");
            setEditingPart(null);
            await load(page);
            api.get("/inventory").then((r) => setInventory(Array.isArray(r.data) ? r.data : (r.data?.items || [])));
            if (issueFor) {
                try {
                    const t = await api.get(`/work-orders/tree?page=${page}&page_size=${PAGE_SIZE}`);
                    const updatedSub = (t.data?.items || []).flatMap((p) => p.sub_tasks).find((s) => s.id === issueFor.id);
                    if (updatedSub) setIssueFor(updatedSub);
                } catch (err) { /* non-fatal */ }
            }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const deletePart = async (subId, idx, line) => {
        if (!window.confirm(`Remove "${line.name}" (${line.quantity} × ₹${line.unit_cost}) from this sub-task?\nStock will be returned to inventory.`)) return;
        try {
            await api.delete(`/work-orders/${subId}/parts/${idx}`);
            toast.success("Part line removed");
            await load(page);
            api.get("/inventory").then((r) => setInventory(Array.isArray(r.data) ? r.data : (r.data?.items || [])));
            if (issueFor) {
                try {
                    const t = await api.get(`/work-orders/tree?page=${page}&page_size=${PAGE_SIZE}`);
                    const updatedSub = (t.data?.items || []).flatMap((p) => p.sub_tasks).find((s) => s.id === issueFor.id);
                    if (updatedSub) setIssueFor(updatedSub);
                } catch (err) { /* non-fatal */ }
            }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    // ----- Filtering ----- (now server-side; `parents` already reflects current page filtered set)
    const hasFilters = search || filterVehicle || filterStatus || filterFrom || filterTo;
    const clearFilters = () => { setSearch(""); setFilterVehicle(""); setFilterStatus(""); setFilterFrom(""); setFilterTo(""); };

    const exportCsv = async () => {
        const allParents = await fetchAllFiltered();
        exportWosCsv(allParents, { filename: `work-orders-${todayISO()}.csv` });
    };

    const fetchAllFiltered = async () => {
        const params = buildFilterParams();
        const r = await api.get(`/work-orders/tree${params.toString() ? `?${params.toString()}` : ""}`);
        return Array.isArray(r.data) ? r.data : (r.data?.items || []);
    };

    const exportPdf = async () => {
        const allParents = await fetchAllFiltered();
        const subCount = allParents.reduce((a, p) => a + (p.sub_tasks || []).length, 0);
        exportWosPdf(allParents, {
            title: `Work Orders · ${allParents.length} main / ${subCount} sub-tasks`,
        });
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in" data-testid="work-orders-page">
            <PageHeader
                eyebrow="MAINTENANCE"
                title="Work Orders"
                subtitle="Each main WO wraps a vehicle visit; add sub-tasks for the actual jobs."
                action={<NewButton onClick={() => { setEditingParent(null); setParentForm(emptyParent); setParentOpen(true); }} label="New main WO" testid="new-parent-wo-btn" />}
            />

            <Surface className="px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search WO #, vehicle, title, description…"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                        data-testid="wo-search-input" />
                </div>
                <select value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink min-w-[140px]"
                    data-testid="wo-filter-vehicle">
                    <option value="">All vehicles</option>
                    {[...vehicles].sort(regSort).map((v) => <option key={v.id} value={v.id}>{v.reg_number}</option>)}
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="wo-filter-status">
                    <option value="">All statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                </select>
                <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                    data-testid="wo-filter-from" title="Created from" />
                <span className="text-xs text-fleet-inkSoft">to</span>
                <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                    data-testid="wo-filter-to" title="Created to" />
                {hasFilters && (
                    <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="wo-clear-filters">
                        <X size={12} /> Clear
                    </button>
                )}
            </Surface>

            <Surface className="px-4 py-2 mb-4">
                <ReportToolbar
                    page={page}
                    totalPages={totalPages}
                    total={total}
                    pageSize={PAGE_SIZE}
                    onPageChange={(p) => { setPage(p); load(p); }}
                    onRefresh={() => load(page)}
                    zoom={zoom}
                    onZoomChange={setZoom}
                    onExportCsv={exportCsv}
                    onExportPdf={exportPdf}
                    busy={busy}
                />
            </Surface>

            <ZoomScope zoom={zoom}>
            <div className="space-y-3">
                {parents.length === 0 && (
                    <Surface className="px-5 py-12 text-center text-sm text-fleet-inkSoft">
                        {total === 0 ? "No work orders yet. Click \"New main WO\" to get started." : "No work orders match the current filters."}
                    </Surface>
                )}
                {parents.map((p) => {
                    const open = !!expanded[p.id];
                    const allDone = p.status === "completed" && p.sub_task_count > 0;
                    return (
                        <Surface key={p.id} className="overflow-hidden" data-testid={`parent-card-${p.id}`}>
                            {/* Parent header */}
                            <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3 flex-wrap">
                                <button
                                    onClick={() => setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))}
                                    className="text-fleet-inkSoft hover:text-fleet-ink"
                                    title={open ? "Collapse" : "Expand"}
                                    data-testid={`toggle-parent-${p.id}`}
                                >
                                    {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
                                </button>
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-sm font-semibold">{p.wo_number}</span>
                                        <span className="text-fleet-inkSoft">·</span>
                                        <span className="font-mono text-sm">{p.vehicle_reg_number || "—"}</span>
                                        <Badge variant={p.status === "completed" ? "healthy" : p.status === "in_progress" ? "info" : "warning"}>
                                            {p.status === "completed" ? "completed" : p.status?.replace("_", " ")}
                                        </Badge>
                                    </div>
                                    <div className="text-xs text-fleet-inkSoft mt-0.5 font-mono">
                                        Created {formatDate(p.creation_date)} · {p.completed_sub_count}/{p.sub_task_count} sub-tasks finished
                                    </div>
                                </div>
                                {allDone && (
                                    <Badge variant="healthy" data-testid={`parent-all-done-${p.id}`}>
                                        <CheckCircle size={12} /> All work finished
                                    </Badge>
                                )}
                                <div className="ml-auto flex items-center gap-2">
                                    <Button variant="secondary" size="sm" onClick={() => openAddSub(p)} data-testid={`add-sub-${p.id}`}>
                                        <Plus size={14} /> Add sub-task
                                    </Button>
                                    <WoExportMenu parent={p} testid={`export-parent-${p.id}`} />
                                    <Button variant="ghost" size="sm" onClick={() => startEditParent(p)} data-testid={`edit-parent-${p.id}`}>
                                        <Pencil size={14} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => deleteParent(p)} data-testid={`del-parent-${p.id}`}>
                                        <Trash size={14} />
                                    </Button>
                                </div>
                            </div>

                            {/* Sub-tasks */}
                            {open && (
                                <div className="divide-y divide-fleet-border">
                                    {Array.isArray(p.descriptions) && p.descriptions.length > 0 && (
                                        <div className="px-5 py-3 bg-slate-50/60" data-testid={`parent-desc-list-${p.id}`}>
                                            <div className="label-mono mb-1.5">Work descriptions</div>
                                            <ul className="space-y-0.5">
                                                {p.descriptions.map((d, di) => (
                                                    <li key={di} className="text-sm text-fleet-ink whitespace-pre-wrap">
                                                        <span className="font-mono text-xs text-fleet-inkMuted mr-2">{formatDate(d.date) || "—"}</span>
                                                        {d.text}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {(p.sub_tasks || []).length === 0 ? (
                                        <div className="px-5 py-8 text-center text-sm text-fleet-inkSoft">
                                            No sub-tasks yet. Click "+ Add sub-task" to begin.
                                        </div>
                                    ) : (
                                        (p.sub_tasks || []).map((s) => (
                                            <div key={s.id} className="px-5 py-3" data-testid={`sub-task-${s.id}`}>
                                                <div className="flex items-start gap-3 flex-wrap">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono text-xs text-fleet-inkSoft">{s.wo_number}</span>
                                                            <span className="text-sm font-medium">{s.title}</span>
                                                            <Badge variant={s.priority}>{s.priority}</Badge>
                                                            <Badge variant={s.type}>{s.type}</Badge>
                                                            <Badge variant={s.status === "done" ? "healthy" : s.status === "in_progress" ? "info" : s.status === "cancelled" ? "low" : "warning"}>
                                                                {s.status?.replace("_", " ")}
                                                            </Badge>
                                                        </div>
                                                        {s.description && (
                                                            <div className="text-sm text-fleet-inkSoft mt-1.5 whitespace-pre-wrap">{s.description}</div>
                                                        )}
                                                        {Array.isArray(s.descriptions) && s.descriptions.length > 0 && (
                                                            <ul className="mt-1.5 space-y-0.5" data-testid={`sub-desc-list-${s.id}`}>
                                                                {s.descriptions.map((d, di) => (
                                                                    <li key={di} className="text-sm text-fleet-inkSoft whitespace-pre-wrap">
                                                                        <span className="font-mono text-xs text-fleet-inkMuted mr-2">{formatDate(d.date) || "—"}</span>
                                                                        {d.text}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <div className="text-xs text-fleet-inkSoft mt-1.5 font-mono flex items-center gap-3 flex-wrap">
                                                            <span><Clock size={11} className="inline mr-1" />Start {formatDate(s.start_date) || "—"}</span>
                                                            {s.end_date && <span className="text-emerald-700">· Finished {formatDate(s.end_date)}</span>}
                                                            <span>· Parts: {(s.parts_issued || []).length}</span>
                                                            <span>· Cost ₹{(s.total_cost || 0).toLocaleString("en-IN")}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <Button variant="secondary" size="sm" onClick={() => setIssueFor(s)} data-testid={`issue-parts-${s.id}`}>
                                                            <Wrench size={12} /> Issue parts
                                                        </Button>
                                                        {s.status !== "done" ? (
                                                            <Button size="sm" onClick={() => { setFinishFor(s); setFinishDate(todayISO()); }} data-testid={`finish-sub-${s.id}`}>
                                                                <CheckCircle size={12} /> Finish
                                                            </Button>
                                                        ) : (
                                                            <Button variant="secondary" size="sm" onClick={() => reopenSub(s)} data-testid={`reopen-sub-${s.id}`}>Re-open</Button>
                                                        )}
                                                        <WoExportMenu parent={p} sub={s} testid={`export-sub-${s.id}`} />
                                                        <Button variant="ghost" size="sm" onClick={() => openEditSub(p, s)} data-testid={`edit-sub-${s.id}`}>
                                                            <Pencil size={12} />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => deleteSub(s)} data-testid={`del-sub-${s.id}`}>
                                                            <Trash size={12} />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* Inline parts list with edit / delete per line */}
                                                {(s.parts_issued || []).length > 0 && (
                                                    <div className="mt-3 border border-fleet-border rounded-md overflow-hidden">
                                                        <table className="w-full text-xs">
                                                            <thead className="bg-slate-50">
                                                                <tr className="text-left">
                                                                    <th className="label-mono px-3 py-1.5 font-normal">PART</th>
                                                                    <th className="label-mono px-3 py-1.5 font-normal">QTY</th>
                                                                    <th className="label-mono px-3 py-1.5 font-normal">UNIT COST</th>
                                                                    <th className="label-mono px-3 py-1.5 font-normal">LINE TOTAL</th>
                                                                    <th className="px-3 py-1.5 no-print"></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(s.parts_issued || []).map((line, i) => (
                                                                    <tr key={`${s.id}-${i}`} className="border-t border-fleet-border" data-testid={`part-line-${s.id}-${i}`}>
                                                                        <td className="px-3 py-1.5">{line.name || line.inventory_id?.slice(0, 8)}</td>
                                                                        <td className="px-3 py-1.5 font-mono">{line.quantity}</td>
                                                                        <td className="px-3 py-1.5 font-mono">₹{(line.unit_cost || 0).toLocaleString("en-IN")}</td>
                                                                        <td className="px-3 py-1.5 font-mono">₹{((line.unit_cost || 0) * (line.quantity || 0)).toLocaleString("en-IN")}</td>
                                                                        <td className="px-3 py-1.5 text-right no-print">
                                                                            <button onClick={() => openEditPart(s.id, i, line)}
                                                                                className="text-xs text-fleet-accent hover:underline mr-2"
                                                                                data-testid={`edit-part-${s.id}-${i}`}>
                                                                                <Pencil size={11} className="inline" /> Edit
                                                                            </button>
                                                                            <button onClick={() => deletePart(s.id, i, line)}
                                                                                className="text-xs text-rose-700 hover:underline"
                                                                                data-testid={`del-part-${s.id}-${i}`}>
                                                                                <Trash size={11} className="inline" /> Remove
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </Surface>
                    );
                })}
            </div>
            </ZoomScope>

            {/* Create / edit PARENT modal */}
            <Modal
                open={parentOpen} onClose={() => { setParentOpen(false); setEditingParent(null); }}
                title={editingParent ? "Edit main work order" : "New main work order"}
                footer={<><Button variant="secondary" onClick={() => { setParentOpen(false); setEditingParent(null); }}>Cancel</Button>
                    <Button onClick={saveParent} data-testid="save-parent-wo">{editingParent ? "Save" : "Create"}</Button></>}
            >
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <VehicleSearchSelect
                            vehicles={vehicles}
                            value={parentForm.vehicle_id}
                            onChange={(id) => setParentForm({ ...parentForm, vehicle_id: id })}
                            label="Vehicle"
                            placeholder="Search by reg #…"
                            testid="parent-vehicle-search"
                        />
                    </div>
                    <Input label="Creation date" type="date"
                        value={parentForm.creation_date}
                        onChange={(e) => setParentForm({ ...parentForm, creation_date: e.target.value })}
                        data-testid="parent-creation-date" />
                    <Input label="Notes (optional)" className="col-span-2"
                        value={parentForm.notes}
                        onChange={(e) => setParentForm({ ...parentForm, notes: e.target.value })} />
                    <div className="col-span-2">
                        <WorkDescriptionsEditor
                            value={parentForm.descriptions}
                            onChange={(next) => setParentForm({ ...parentForm, descriptions: next })}
                            label="Work descriptions (dated)"
                            testidPrefix="parent-desc"
                        />
                    </div>
                </div>
                <div className="mt-2 text-xs text-fleet-inkSoft">
                    The main WO is a container — add sub-tasks below it for individual jobs. Status auto-rolls up from sub-tasks.
                </div>
            </Modal>

            {/* Create / edit SUB-TASK modal */}
            <Modal
                open={subOpen} onClose={() => { setSubOpen(false); setEditingSub(null); }}
                title={editingSub ? "Edit sub-task" : "Add sub-task"}
                footer={<><Button variant="secondary" onClick={() => { setSubOpen(false); setEditingSub(null); }}>Cancel</Button>
                    <Button onClick={saveSub} data-testid="save-sub-wo">{editingSub ? "Save" : "Add"}</Button></>}
            >
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Title" className="col-span-2"
                        value={subForm.title}
                        onChange={(e) => setSubForm({ ...subForm, title: e.target.value })}
                        placeholder="e.g. Engine oil + filter change"
                        data-testid="sub-title" />
                    <Input label="Start date" type="date"
                        value={subForm.start_date}
                        onChange={(e) => setSubForm({ ...subForm, start_date: e.target.value })}
                        data-testid="sub-start-date" />
                    <Select label="Type" value={subForm.type}
                        onChange={(e) => setSubForm({ ...subForm, type: e.target.value })}
                        options={TYPES} />
                    <Select label="Priority" value={subForm.priority}
                        onChange={(e) => setSubForm({ ...subForm, priority: e.target.value })}
                        options={PRIORITIES} />
                    <div className="col-span-2">
                        <WorkDescriptionsEditor
                            value={subForm.descriptions}
                            onChange={(next) => setSubForm({ ...subForm, descriptions: next })}
                            label="Work descriptions (dated)"
                            testidPrefix="sub-desc"
                        />
                    </div>
                </div>
            </Modal>

            {/* Finish sub-task modal */}
            <Modal
                open={!!finishFor} onClose={() => setFinishFor(null)}
                title={finishFor ? `Finish ${finishFor.wo_number}` : ""}
                footer={<><Button variant="secondary" onClick={() => setFinishFor(null)}>Cancel</Button>
                    <Button onClick={finishSub} data-testid="confirm-finish-sub">Mark as finished</Button></>}
            >
                {finishFor && (
                    <div className="space-y-3">
                        <div className="text-sm">
                            <div className="text-fleet-inkSoft">Sub-task</div>
                            <div className="font-medium">{finishFor.title}</div>
                        </div>
                        <Input label="Finish date" type="date" value={finishDate}
                            onChange={(e) => setFinishDate(e.target.value)} data-testid="finish-date" />
                    </div>
                )}
            </Modal>

            {/* Issue parts modal (per sub-task) */}
            <Modal
                open={!!issueFor} onClose={() => { setIssueFor(null); setIssuePart({ inventory_id: "", quantity: 1 }); }}
                title={issueFor ? `Parts on ${issueFor.wo_number} · ${issueFor.title}` : ""}
                size="lg"
                footer={<Button variant="secondary" onClick={() => { setIssueFor(null); setIssuePart({ inventory_id: "", quantity: 1 }); }}>Close</Button>}
            >
                {issueFor && (
                    <div className="space-y-4">
                        <div className="border border-fleet-border rounded-md p-3 bg-slate-50/60">
                            <div className="label-mono mb-2">ISSUE NEW PART</div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <Select label="Part" value={issuePart.inventory_id}
                                        onChange={(e) => setIssuePart({ ...issuePart, inventory_id: e.target.value })}
                                        options={[{ value: "", label: "Select part…" }, ...inventory.map((i) => ({ value: i.id, label: `${i.sku} · ${i.name} (${i.quantity} in stock)` }))]} />
                                </div>
                                <Input label="Quantity" type="number" min="1"
                                    value={issuePart.quantity}
                                    onChange={(e) => setIssuePart({ ...issuePart, quantity: e.target.value })} />
                            </div>
                            <div className="flex justify-end mt-3">
                                <Button onClick={issueParts} data-testid="confirm-issue-parts">Issue part</Button>
                            </div>
                        </div>

                        {(issueFor.parts_issued || []).length === 0 ? (
                            <div className="text-center py-6 text-sm text-fleet-inkSoft border border-fleet-border rounded-md">No parts issued yet.</div>
                        ) : (
                            <div className="border border-fleet-border rounded-md overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr className="text-left">
                                            <th className="label-mono px-3 py-1.5 font-normal">PART</th>
                                            <th className="label-mono px-3 py-1.5 font-normal">QTY</th>
                                            <th className="label-mono px-3 py-1.5 font-normal">UNIT COST</th>
                                            <th className="label-mono px-3 py-1.5 font-normal">TOTAL</th>
                                            <th className="px-3 py-1.5"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(issueFor.parts_issued || []).map((line, i) => (
                                            <tr key={`${issueFor.id}-pl-${i}`} className="border-t border-fleet-border">
                                                <td className="px-3 py-1.5">{line.name || line.inventory_id?.slice(0, 8)}</td>
                                                <td className="px-3 py-1.5 font-mono">{line.quantity}</td>
                                                <td className="px-3 py-1.5 font-mono">₹{(line.unit_cost || 0).toLocaleString("en-IN")}</td>
                                                <td className="px-3 py-1.5 font-mono">₹{((line.unit_cost || 0) * (line.quantity || 0)).toLocaleString("en-IN")}</td>
                                                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                                    <button onClick={() => openEditPart(issueFor.id, i, line)}
                                                        className="text-xs text-fleet-accent hover:underline mr-2">
                                                        <Pencil size={11} className="inline" /> Edit
                                                    </button>
                                                    <button onClick={() => deletePart(issueFor.id, i, line)}
                                                        className="text-xs text-rose-700 hover:underline">
                                                        <Trash size={11} className="inline" /> Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Edit a single part line (qty / unit cost) */}
            <Modal
                open={!!editingPart} onClose={() => setEditingPart(null)}
                title={editingPart ? `Edit part · ${editingPart.line?.name || ""}` : ""}
                footer={<><Button variant="secondary" onClick={() => setEditingPart(null)}>Cancel</Button>
                    <Button onClick={saveEditPart} data-testid="confirm-edit-part">Save</Button></>}
            >
                {editingPart && (
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Quantity" type="number" min="1"
                            value={editPartForm.quantity}
                            onChange={(e) => setEditPartForm({ ...editPartForm, quantity: e.target.value })} />
                        <Input label="Unit cost (₹)" type="number" step="0.01" min="0"
                            value={editPartForm.unit_cost}
                            onChange={(e) => setEditPartForm({ ...editPartForm, unit_cost: e.target.value })} />
                        <div className="col-span-2 text-xs text-fleet-inkSoft">
                            Adjusting the quantity will rebalance the inventory stock and record a transaction so the audit trail stays clean.
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
